import { useState, useEffect, useMemo, useCallback, useRef, Suspense } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Check, Play, Circle } from '@phosphor-icons/react'
import type { Stage, ABCDObjective } from '@jw-av/shared'
import { api } from '../lib/api'
import { ModuleMdxProvider } from '../components/ModuleMdxProvider'
import type { ModuleViewMode } from '../components/ModuleMdxProvider'
import { DeckProvider, SlideDeck } from '../components/SlideDeck'
import { ScrollViewer } from '../components/ScrollViewer'
import { KcRegistryContext } from '../components/KcRegistryContext'
import { getKcAttempts, KC_ATTEMPT_EVENT, mergeServerKcAttempts } from '../lib/kcAttempts'
import moduleImports from '../generated/module-imports'
import { handCraftedModules } from '../modules'
import type { HandCraftedModule } from '../components/slides/types'
import { useOfflineStatus } from '../hooks/useOfflineStatus'
import { useCachedModule } from '../hooks/useCachedModule'
import { OfflineIndicator } from '../components/OfflineIndicator'

const VIEW_PREF_KEY = 'module-view-preference'

function readViewPreference(): ModuleViewMode {
  try {
    const raw = localStorage.getItem(VIEW_PREF_KEY)
    if (raw === 'scroll' || raw === 'deck') return raw
  } catch {
    // ignore
  }
  return 'deck'
}

function writeViewPreference(mode: ModuleViewMode): void {
  try {
    localStorage.setItem(VIEW_PREF_KEY, mode)
  } catch {
    // ignore
  }
}

const STAGE_LABELS: Record<Stage, string> = {
  foundation: 'Foundation',
  core: 'Core',
  advanced: 'Advanced',
  enrichment: 'Enrichment',
}

interface Module {
  id: string
  title: string
  crew: string
  teams: string[]
  roles: string[]
  stage: Stage
  wiifm: string
  sources: Record<string, string>[]
  slideCount: number
  objectives: ABCDObjective[]
}

export default function ModulePage() {
  const { moduleId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const viewParam = searchParams.get('view')
  const viewMode: ModuleViewMode =
    viewParam === 'scroll' || viewParam === 'deck' ? viewParam : readViewPreference()
  useEffect(() => {
    writeViewPreference(viewMode)
  }, [viewMode])
  const [module, setModule] = useState<Module | null>(null)
  const [progress, setProgress] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [MdxComponent, setMdxComponent] = useState<React.ComponentType | null>(null)
  const [handCrafted, setHandCrafted] = useState<HandCraftedModule | null>(null)
  const [kcIds, setKcIds] = useState<Set<string>>(() => new Set())
  const [handoutFilename, setHandoutFilename] = useState<string | null>(null)
  const kcIdsRef = useRef(kcIds)
  const [attemptsTick, setAttemptsTick] = useState(0)
  const { isOnline } = useOfflineStatus()
  const { getCachedModule, cacheModule } = useCachedModule()

  const registerKc = useCallback((kcId: string) => {
    if (kcIdsRef.current.has(kcId)) return
    const next = new Set(kcIdsRef.current)
    next.add(kcId)
    kcIdsRef.current = next
    setKcIds(next)
  }, [])

  const registry = useMemo(() => ({ register: registerKc }), [registerKc])

  useEffect(() => {
    kcIdsRef.current = new Set()
    setKcIds(new Set())
  }, [moduleId])

  useEffect(() => {
    function onAttempt(event: Event) {
      const detail = (event as CustomEvent<{ moduleId: string }>).detail
      if (detail?.moduleId === moduleId) {
        setAttemptsTick((t) => t + 1)
      }
    }
    window.addEventListener(KC_ATTEMPT_EVENT, onAttempt)
    return () => window.removeEventListener(KC_ATTEMPT_EVENT, onAttempt)
  }, [moduleId])

  const canComplete = useMemo(() => {
    if (!moduleId) return false
    if (kcIds.size === 0) return true
    const attempts = getKcAttempts(moduleId)
    for (const id of kcIds) {
      if (!attempts[id]) return false
    }
    return true
  }, [moduleId, kcIds, attemptsTick])

  useEffect(() => {
    async function loadData() {
      if (!moduleId) return
      
      try {
        setLoading(true)
        
        let moduleData: any = null
        let progressData: any = null

        if (!isOnline) {
          // Offline: try to load from IndexedDB cache
          const cached = await getCachedModule(moduleId)
          if (cached) {
            moduleData = { module: cached.metadata }
            progressData = { progress: [] }
            // Restore cached content if available
            if (cached.content?.type === 'handcrafted') {
              setHandCrafted(cached.content.data)
              setMdxComponent(null)
            } else if (cached.content?.type === 'mdx') {
              setHandCrafted(null)
              // MDX components can't be serialized; show placeholder when offline
              setMdxComponent(null)
            }
          } else {
            setError('This module is not available offline. Please connect to the internet to load it.')
            setLoading(false)
            return
          }
        } else {
          // Online: fetch from API
          moduleData = await api.getModule(moduleId)
          progressData = await api.getProgress()

          // Cache module data for offline use
          const contentToCache: any = { type: null, data: null }

          // Hand-crafted module registry takes precedence (ADR-0018).
          const hcImport = handCraftedModules[moduleId]
          if (hcImport) {
            const hc = await hcImport()
            setHandCrafted({ meta: hc.meta, slides: hc.slides })
            setMdxComponent(null)
            contentToCache.type = 'handcrafted'
            contentToCache.data = { meta: hc.meta, slides: hc.slides }
          } else {
            setHandCrafted(null)
            const importFn = moduleImports[moduleId]
            if (importFn) {
              const mdxModule = await importFn()
              setMdxComponent(() => mdxModule.default)
              contentToCache.type = 'mdx'
            }
          }

          // Store in IndexedDB for offline access
          await cacheModule(moduleId, moduleData.module, contentToCache)
        }

        setModule(moduleData.module)
        
        const moduleProgress = progressData.progress?.find((p: any) => p.moduleId === moduleId)
        setProgress(moduleProgress || null)

        // Check for handout
        try {
          const manifest = await fetch('/handouts/manifest.json').then((r) => r.json())
          setHandoutFilename(manifest[moduleId] ?? null)
        } catch {
          setHandoutFilename(null)
        }

        // Hydrate KC attempts from server so a fresh device can resume.
        // Only attempt when online; failures are non-fatal.
        if (isOnline) {
          api
            .getKnowledgeCheckAttempts({ module_id: moduleId })
            .then((res: { attempts: Parameters<typeof mergeServerKcAttempts>[1] }) => {
              const changed = mergeServerKcAttempts(moduleId, res.attempts ?? [])
              if (changed) setAttemptsTick((t) => t + 1)
            })
            .catch((err) => {
              console.warn('[kc] failed to hydrate attempts from server', err)
            })
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load module')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [moduleId, isOnline, getCachedModule, cacheModule])

  async function markComplete() {
    if (!moduleId || !isOnline) return
    try {
      const data = await api.updateProgress({
        module_id: moduleId,
        status: 'completed',
        percent_complete: 100,
      })
      setProgress(data.progress)
    } catch (err) {
      console.error('Failed to update progress:', err)
    }
  }

  async function markInProgress() {
    if (!moduleId || !isOnline) return
    try {
      const data = await api.updateProgress({
        module_id: moduleId,
        status: 'in_progress',
        percent_complete: 50,
      })
      setProgress(data.progress)
    } catch (err) {
      console.error('Failed to update progress:', err)
    }
  }

  if (loading) {
    return (
      <div className="module-page">
        <p>Loading module...</p>
      </div>
    )
  }

  if (error || !module) {
    return (
      <div className="module-page">
        <h2>Module not found</h2>
        {error && <p className="error">{error}</p>}
      </div>
    )
  }

  return (
    <div className="module-page">
      <div className="module-header">
        <div className="module-meta">
          <span>{module.crew}</span>
          <span>{module.teams.join(' / ')}</span>
          <span>{STAGE_LABELS[module.stage]}</span>
        </div>
        <h2>{module.title}</h2>
        <p className="module-wiifm">{module.wiifm}</p>
        <OfflineIndicator />
      </div>

      <div className="module-progress">
        <div className="progress-actions">
          {progress?.status === 'completed' ? (
            <div className="status-badge status-completed"><Check size={16} weight="bold" /> Completed</div>
          ) : progress?.status === 'in_progress' ? (
            <div className="status-badge status-in-progress"><Play size={16} weight="fill" /> In Progress</div>
          ) : (
            <div className="status-badge status-not-started"><Circle size={16} /> Not Started</div>
          )}

          <div className="action-buttons">
            {handoutFilename && (
              <a
                href={`/handouts/${handoutFilename}.pdf`}
                download
                className="btn btn-secondary"
              >
                Download Handout
              </a>
            )}
            {progress?.status !== 'in_progress' && progress?.status !== 'completed' && (
              <button onClick={markInProgress} className="btn btn-primary" disabled={!isOnline} title={isOnline ? undefined : 'Unavailable offline'}>
                Start Module
              </button>
            )}
            {progress?.status !== 'completed' && (
              <button
                onClick={markComplete}
                className="btn btn-success"
                disabled={!canComplete || !isOnline}
                title={!isOnline ? 'Unavailable offline' : canComplete ? undefined : 'Attempt all knowledge checks to finish.'}
              >
                Mark Complete
              </button>
            )}
          </div>
        </div>
      </div>

      {handCrafted ? (
        <DeckProvider
          moduleTitle={module.title}
          onExit={() => navigate(-1)}
          moduleId={module.id}
          serverIndex={progress?.lastSlideIndex}
        >
          <KcRegistryContext.Provider value={registry}>
            {viewMode === 'scroll' ? (
              <ScrollViewer slides={handCrafted.slides} />
            ) : (
              <SlideDeck slides={handCrafted.slides} />
            )}
          </KcRegistryContext.Provider>
        </DeckProvider>
      ) : MdxComponent ? (
        <DeckProvider
          moduleTitle={module.title}
          onExit={() => navigate(-1)}
          moduleId={module.id}
          serverIndex={progress?.lastSlideIndex}
        >
          <KcRegistryContext.Provider value={registry}>
            <ModuleMdxProvider viewMode={viewMode}>
              <Suspense fallback={<div>Loading content...</div>}>
                <MdxComponent />
              </Suspense>
            </ModuleMdxProvider>
          </KcRegistryContext.Provider>
        </DeckProvider>
      ) : !isOnline ? (
        <div className="module-content">
          <div className="content-placeholder">
            <p>Module content is not available offline.</p>
            <p>Please connect to the internet to view this module's interactive content.</p>
            <p>Module ID: {module.id}</p>
          </div>
        </div>
      ) : (
        <div className="module-content">
          <div className="content-placeholder">
            <p>Module content will be rendered here from MDX.</p>
            <p>Module ID: {module.id}</p>
            <p>Roles: {module.roles.join(', ')}</p>
            <p>Slide count: {module.slideCount}</p>
          </div>
        </div>
      )}

      {(() => {
        // Editorial "[TODO: verify …]" markers are authoring metadata, not
        // citations — never show them to trainees.
        const visibleSources = module.sources
          .map((source: Record<string, string>) => {
            const [key, value] = Object.entries(source)[0]
            const cleaned = value
              .split(';')
              .map((part: string) => part.trim())
              .filter((part: string) => !part.startsWith('[TODO'))
              .join('; ')
            return [key, cleaned] as const
          })
          .filter(([, value]: readonly [string, string]) => value.length > 0)
        if (visibleSources.length === 0) return null
        return (
          <div className="module-sources">
            <h3>Sources</h3>
            <ul>
              {visibleSources.map(([key, value]: readonly [string, string], idx: number) => (
                <li key={idx}>
                  <strong>{key.toUpperCase()}</strong>: {value}
                </li>
              ))}
            </ul>
          </div>
        )
      })()}

      <div className="module-navigation">
        <button onClick={() => navigate(-1)} className="btn btn-secondary">
          ← Back
        </button>
      </div>
    </div>
  )
}
